import { requireUserSiteFieldsReady } from "./userSiteFields.js";
/**
 * @file User invite modal submit owner.
 */

import { appData, state } from "../../app/state/state.js";
import {
    createUserInvite,
    sendUserInvite,
    updateUserInvite,
} from "./userLifecycleApi.js";
import { getNormalizedUserPermissions } from "./permissionModel.js";

function upsertLocalUserInvite(inviteData) {
    if (!inviteData?.id) {return;}

    const nextInvites = Array.isArray(appData.userInvites)
        ? [...appData.userInvites]
        : [];
    const existingIndex = nextInvites.findIndex((invite) =>
        invite.id === inviteData.id);

    if (existingIndex === -1) {
        nextInvites.unshift(inviteData);
    } else {
        nextInvites[existingIndex] = {
            ...nextInvites[existingIndex],
            ...inviteData,
        };
    }

    appData.userInvites = nextInvites;
}

/**
 * Returns friendly invite conflict copy for known callable errors.
 * @param {string} message Error message.
 * @returns {{title: string, body: string}|null} Modal copy.
 */
export function getUserInviteConflictModalConfig(message) {
    const normalizedMessage = String(message || "").toLowerCase();
    if (normalizedMessage.includes("invite for this email already exists")) {
        return {
            title: "Error: Invite already exists",
            body: "An invite for this email already exists. You can resend the invite by clicking on the email button.",
        };
    }
    if (normalizedMessage.includes("inactive user with the same email exists")) {
        return {
            title: "Error: User already exists",
            body: "An inactive user with the same email exists. If you are inviting the same person, reactivate their account instead. If this is a different person, use a different email.",
        };
    }
    if (normalizedMessage.includes("active user with the same email exists")) {
        return {
            title: "Error: User already exists",
            body: "An active user with the same email exists.",
        };
    }
    return null;
}

/**
 * Saves a user invite form payload and optionally sends the invite.
 * @param {object} params Submit context.
 * @param {object} params.data Form payload.
 * @param {FormData} params.formData Raw form data.
 * @param {HTMLFormElement} params.form Source form.
 * @param {string} params.id Existing invite ID.
 * @param {string} params.submitAction Submit action.
 * @returns {Promise<void>}
 */
export async function saveUserInviteForm({
    data,
    formData,
    form,
    id,
    submitAction,
}) {
    requireUserSiteFieldsReady(form);
    data.permissions = getNormalizedUserPermissions(
        data.role,
        Array.from(formData.getAll("permissions")),
    );
    data.isAppProvider = form.elements.isAppProvider?.checked === true;

    const inviteBaseData = {
        id: id || data.email,
        email: data.email,
        name: data.name || "",
        role: data.role || "user",
        permissions: data.permissions || [],
        managerUserId: data.managerUserId || "",
        isAppProvider: data.isAppProvider === true,
        updatedBy: state.currentUser?.uid || null,
    };

    const savedInvite = id
        ? await updateUserInvite(id, data)
        : await createUserInvite(data);
    const inviteId = savedInvite?.id || inviteBaseData.id;

    upsertLocalUserInvite({
        ...inviteBaseData,
        ...savedInvite,
        id: inviteId,
    });

    if (submitAction !== "send") {return;}

    const sendResult = await sendUserInvite(inviteId);
    upsertLocalUserInvite({
        ...inviteBaseData,
        ...savedInvite,
        ...sendResult,
        id: inviteId,
        status: sendResult?.status || "sent",
        lastSentAt: new Date(),
        lastSentBy: state.currentUser?.uid || null,
    });
}
